import type { IConversationMessage, RulebookDisplayedSnapshot } from "@next-platform/client-common-core";
import {
  mergeRevealedLevels,
  useConversationRulebookSettings,
  useDisplayedRulebookSnapshot,
  usePersona,
  useResolvedRulebook,
  useTranslation,
} from "@next-platform/client-common-core";
import hudTileText from "@next-platform/client-common-core/rulebook/hud/hudTileText";
import resolveHudMeterValue from "@next-platform/client-common-core/rulebook/hud/resolveHudMeterValue";
import useArrivedSnapshotId from "@next-platform/client-common-core/rulebook/hud/useArrivedSnapshotId";
import useHudMinimized from "@next-platform/client-common-core/rulebook/hud/useHudMinimized";
import { useMedia } from "@next-platform/client-common-spicy-ui";
import type { RulebookPublicView } from "@next-platform/packages-ts-types";
import React, { useCallback, useMemo, useState } from "react";

import Trans from "@/components/Trans/Trans";

import RulebookExplanationSheet from "./RulebookExplanationSheet";
import RulebookHudCaption from "./RulebookHudCaption";
import RulebookHudChip from "./RulebookHudChip";
import RulebookMeterPanel, { type HudPanelMeter } from "./RulebookMeterPanel";

/** The top panel shows at most four meters on every device; the sheet lists them all. */
const PANEL_MAX_METERS = 4;

type RulebookHudContentProps = {
  characterId?: string;
  characterName?: string;
  conversationId?: string;
  conversation: IConversationMessage[];
  altMsgs: IConversationMessage[];
  altMsgPosition: number;
  /** Whether `conversation` reflects a loaded thread — a brand-new chat counts as loaded (shows turn-0). */
  isConversationLoaded?: boolean;
};

/** Resolved rulebook with revealed-level text merged in, plus the first PANEL_MAX_METERS tiles for the strip. */
const usePanelMeters = ({ rulebook, snapshot }: { rulebook: RulebookPublicView | null | undefined; snapshot: RulebookDisplayedSnapshot | null }) => {
  const { t } = useTranslation();
  // The public view withholds unrevealed level text; the snapshot restores the text of reached levels.
  const displayedRulebook = useMemo(
    () => (rulebook ? mergeRevealedLevels({ rulebook, snapshot: snapshot?.meta ?? null }) : rulebook),
    [rulebook, snapshot],
  );
  const panelMeters = useMemo<HudPanelMeter[]>(() => {
    if (!displayedRulebook || !snapshot) {
      return [];
    }
    const levelText = (levelNumber: number) => t("chat:rulebook.hud.levelShort", { defaultValue: "Lv {{number}}", number: levelNumber });
    return (displayedRulebook.meters ?? []).slice(0, PANEL_MAX_METERS).map((meter) => {
      const value = resolveHudMeterValue({ meter, hudMeter: snapshot.meta.meters?.[meter.id] });
      return { meter, value, text: hudTileText({ meter, value, clockLabel: snapshot.meta.clock?.label, levelText }) };
    });
  }, [displayedRulebook, snapshot, t]);
  return { displayedRulebook, panelMeters };
};

const RulebookHudContent = ({
  characterId,
  characterName,
  conversationId,
  conversation,
  altMsgs,
  altMsgPosition,
  isConversationLoaded,
}: RulebookHudContentProps) => {
  const { isMobile } = useMedia();
  const { settings, setFrozen, setHudHidden } = useConversationRulebookSettings({ conversationId });
  const { isMinimized, setIsMinimized } = useHudMinimized();
  const { selectedPersona } = usePersona();
  // The conversation's own Rulebook (pinned) once hydrated; else the selected persona's, else the character's.
  const { rulebook, initialState } = useResolvedRulebook({ characterId, conversationId, personaId: selectedPersona?.id });
  const snapshot = useDisplayedRulebookSnapshot({
    conversation,
    altMsgs,
    altMsgPosition,
    initialState,
    rulebookId: rulebook?.id,
    isEnabled: true,
    isConversationLoaded,
  });
  const arrivedMessageId = useArrivedSnapshotId({ snapshot, conversation, altMsgs });
  const [stateIsOpen, setStateIsOpen] = useState(false);

  const handleOpenPanel = useCallback(() => setStateIsOpen(true), []);
  const handleClosePanel = useCallback(() => setStateIsOpen(false), []);
  const handleMinimize = useCallback(() => setIsMinimized(true), [setIsMinimized]);
  const handleExpand = useCallback(() => setIsMinimized(false), [setIsMinimized]);
  // Hidden meters keep updating and shaping replies; the chat menu's "Show meters" brings them back.
  const handleToggleHidden = useCallback(
    (isHidden: boolean) => {
      if (isHidden) {
        setStateIsOpen(false);
      }
      setHudHidden(isHidden);
    },
    [setHudHidden],
  );
  const handleShow = useCallback(() => setHudHidden(false), [setHudHidden]);

  const canUpdateSettings = Boolean(conversationId);
  const { displayedRulebook, panelMeters } = usePanelMeters({ rulebook, snapshot });

  if (!displayedRulebook || !snapshot) {
    return null;
  }
  // Hidden meters still evaluate; the caption line is the way back when the chat menu's "Show meters" is missed.
  if (settings.hud_hidden === true) {
    return <RulebookHudCaption rulebook={displayedRulebook} snapshot={snapshot} characterName={characterName} onShow={handleShow} />;
  }
  const isArrived = arrivedMessageId === snapshot.messageId;
  let panel: React.ReactNode = null;
  if (panelMeters.length > 0) {
    panel = isMinimized ? (
      <RulebookHudChip meters={panelMeters} snapshot={snapshot} isArrived={isArrived} onOpenPanel={handleOpenPanel} onExpand={handleExpand} />
    ) : (
      <RulebookMeterPanel
        meters={panelMeters}
        snapshot={snapshot}
        isArrived={isArrived}
        isMobile={isMobile}
        onOpenPanel={handleOpenPanel}
        onMinimize={handleMinimize}
      />
    );
  }

  return (
    <>
      {settings.frozen === true ? (
        <span
          data-testid="RulebookHud-FrozenBadge"
          className="inline-flex items-center gap-1 rounded-full bg-gray-3 px-2 py-0.5 text-[11px] text-gray-11 dark:bg-gray-5"
        >
          <Trans i18nKey="chat:rulebook.hud.frozen" defaults="Frozen" />
        </span>
      ) : null}
      {panel}
      <RulebookExplanationSheet
        rulebook={displayedRulebook}
        snapshot={snapshot}
        characterName={characterName}
        isMobile={isMobile}
        open={stateIsOpen}
        handleClose={handleClosePanel}
        isFrozen={settings.frozen === true}
        settingsEnabled={canUpdateSettings}
        onToggleFrozen={setFrozen}
        onToggleHidden={handleToggleHidden}
      />
    </>
  );
};

export default React.memo(RulebookHudContent);
